/**
 * TrainingPanel Component (Feature #56)
 *
 * Training mode UI: scenario selection, briefing, objectives, timer, scoring.
 * Integrates with the chatbot window as a separate view/tab.
 * Includes localStorage persistence to survive page refreshes.
 *
 * @module TrainingPanel
 */

'use client';

import React, { useEffect, useCallback, useState, useRef } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Heading,
  type BadgeColorPalette,
} from '@axon/ui';
import {
  LuGraduationCap,
  LuPlay,
  LuCircleCheck,
  LuClock,
  LuTarget,
  LuTrophy,
  LuX,
} from 'react-icons/lu';
import { useTranslations } from 'next-intl';

import { useTraining, type TrainingScenarioSummary } from '../hooks/useTraining';

// ============================================================================
// CONSTANTS
// ============================================================================

const DIFFICULTY_COLORS: Record<string, BadgeColorPalette> = {
  beginner: 'green',
  intermediate: 'orange',
  advanced: 'red',
};

const CATEGORY_KEYS: Record<string, string> = {
  dispatch: 'categories.dispatch',
  order_management: 'categories.order_management',
  fleet: 'categories.fleet',
  finance: 'categories.finance',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function TrainingPanel(): React.ReactElement {
  const t = useTranslations('chatbot.training');
  const {
    scenarios,
    activeSession,
    lastScore,
    progress,
    isLoading,
    error,
    elapsedSeconds: persistedElapsed,
    loadScenarios,
    startSession,
    submitForScoring,
    loadProgress,
    clearSession,
    updateElapsedTime,
  } = useTraining();

  // Local timer state - initialize with 0, will be set from persisted value in effect
  const [localElapsed, setLocalElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasInitializedRef = useRef(false);

  // Load scenarios and progress on mount
  useEffect(() => {
    void loadScenarios();
    void loadProgress();
  }, [loadScenarios, loadProgress]);

  // Initialize local elapsed from persisted value when component mounts with restored session
  useEffect(() => {
    if (activeSession && !hasInitializedRef.current && persistedElapsed > 0) {
      setLocalElapsed(persistedElapsed);
    }
  }, [activeSession, persistedElapsed]);

  // Timer for active session
  useEffect(() => {
    if (activeSession) {
      // Only initialize from persisted value once per session
      if (!hasInitializedRef.current) {
        setLocalElapsed(persistedElapsed);
        hasInitializedRef.current = true;
      }

      timerRef.current = setInterval(() => {
        setLocalElapsed((prev) => {
          const newValue = prev + 1;
          // Persist every 10 seconds to reduce localStorage writes
          if (newValue % 10 === 0) {
            updateElapsedTime(newValue);
          }
          return newValue;
        });
      }, 1000);
    } else {
      // Clear timer and reset initialization flag when no active session
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      hasInitializedRef.current = false;
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // Note: persistedElapsed intentionally excluded to avoid race condition when session changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, updateElapsedTime]);

  const handleStart = useCallback(
    async (scenarioId: string) => {
      setLocalElapsed(0);
      await startSession(scenarioId);
    },
    [startSession]
  );

  const handleSubmit = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // Persist final elapsed time before submit
    updateElapsedTime(localElapsed);
    await submitForScoring();
  }, [submitForScoring, updateElapsedTime, localElapsed]);

  const handleClearSession = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setLocalElapsed(0);
    clearSession();
  }, [clearSession]);

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ---- SCORE VIEW ----
  if (lastScore) {
    return (
      <Box padding="4" height="full" overflowY="auto">
        <VStack gap="4" align="stretch">
          <HStack gap="2" justify="space-between">
            <HStack gap="2">
              <LuTrophy size={20} />
              <Heading size="md">{t('resultsTitle')}</Heading>
            </HStack>
            <Button size="xs" variant="ghost" onClick={handleClearSession} aria-label={t('cancelSession')}>
              <LuX size={14} />
            </Button>
          </HStack>

          <Box bg="bg.subtle" padding="4" borderRadius="lg" textAlign="center">
            <Text
              fontSize="3xl"
              fontWeight="bold"
              color={lastScore.overallScore >= 70 ? 'green.500' : 'orange.500'}
            >
              {lastScore.overallScore}/100
            </Text>
            <Text color="fg.muted" fontSize="sm">
              {t('overallScore')}
            </Text>
          </Box>

          <VStack gap="2" align="stretch">
            <ScoreBar label={t('scores.taskCompletion')} value={lastScore.scores.taskCompletion} />
            <ScoreBar label={t('scores.efficiency')} value={lastScore.scores.efficiency} />
            <ScoreBar label={t('scores.correctness')} value={lastScore.scores.correctness} />
            <ScoreBar label={t('scores.timeManagement')} value={lastScore.scores.timeManagement} />
          </VStack>

          <Box bg="bg.subtle" padding="3" borderRadius="md">
            <Text fontSize="sm">{lastScore.feedback}</Text>
          </Box>

          {lastScore.strengths.length > 0 && (
            <Box>
              <Text fontWeight="semibold" fontSize="sm" marginBottom="1">
                {t('strengths')}
              </Text>
              {lastScore.strengths.map((s, i) => (
                <Text key={i} fontSize="sm" color="green.600">
                  + {s}
                </Text>
              ))}
            </Box>
          )}

          {lastScore.improvements.length > 0 && (
            <Box>
              <Text fontWeight="semibold" fontSize="sm" marginBottom="1">
                {t('areasForImprovement')}
              </Text>
              {lastScore.improvements.map((s, i) => (
                <Text key={i} fontSize="sm" color="orange.600">
                  - {s}
                </Text>
              ))}
            </Box>
          )}

          <Text fontSize="xs" color="fg.muted">
            {t('timeSpent', { time: formatTime(lastScore.timeSpentSeconds) })}
          </Text>
        </VStack>
      </Box>
    );
  }

  // ---- ACTIVE SESSION VIEW ----
  if (activeSession) {
    const timeLimit = activeSession.timeLimitSeconds;
    const isOverTime = localElapsed > timeLimit;

    return (
      <Box padding="4" height="full" overflowY="auto">
        <VStack gap="4" align="stretch">
          <HStack gap="2" justify="space-between">
            <HStack gap="2">
              <LuTarget size={18} />
              <Text fontWeight="semibold" fontSize="sm">
                {t('trainingActive')}
              </Text>
            </HStack>
            <HStack gap="1">
              <LuClock size={14} />
              <Text fontSize="sm" fontWeight="bold" color={isOverTime ? 'red.500' : 'fg.default'}>
                {formatTime(localElapsed)} / {formatTime(timeLimit)}
              </Text>
            </HStack>
          </HStack>

          <Box bg="blue.50" padding="3" borderRadius="md">
            <Text fontSize="sm">{activeSession.briefing}</Text>
          </Box>

          <VStack gap="2" align="stretch">
            <Text fontWeight="semibold" fontSize="sm">
              {t('objectives')}:
            </Text>
            {activeSession.objectives.map((obj) => (
              <HStack key={obj.id} gap="2" fontSize="sm">
                <LuCircleCheck size={14} />
                <Text flex="1">{obj.description}</Text>
                <Badge size="sm" colorPalette="gray">
                  {obj.weight}%
                </Badge>
              </HStack>
            ))}
          </VStack>

          <HStack gap="2">
            <Button variant="outline" size="sm" onClick={handleClearSession} flex="1">
              {t('cancelSession')}
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleSubmit}
              isLoading={isLoading}
              flex="2"
            >
              {t('submitForScoring')}
            </Button>
          </HStack>
        </VStack>
      </Box>
    );
  }

  // ---- SCENARIO LIST VIEW ----
  return (
    <Box padding="4" height="full" overflowY="auto">
      <VStack gap="4" align="stretch">
        <HStack gap="2">
          <LuGraduationCap size={20} />
          <Heading size="md">{t('title')}</Heading>
        </HStack>

        {progress && progress.completedScenarios.length > 0 && (
          <Box bg="bg.subtle" padding="3" borderRadius="md">
            <HStack justify="space-between">
              <Text fontSize="sm">
                {t('completedCount', { completed: progress.completedScenarios.length, total: scenarios.length })}
              </Text>
              <Text fontSize="sm" fontWeight="bold">
                {t('totalPts', { score: progress.totalScore })}
              </Text>
              {progress.streak > 1 && (
                <Badge colorPalette="orange" size="sm">
                  {t('dayStreak', { count: progress.streak })}
                </Badge>
              )}
            </HStack>
          </Box>
        )}

        {error && (
          <Box bg="red.50" padding="2" borderRadius="md">
            <Text fontSize="sm" color="red.500">
              {error}
            </Text>
          </Box>
        )}

        {scenarios.map((scenario) => (
          <ScenarioCard
            key={scenario.id}
            scenario={scenario}
            bestScore={progress?.scores[scenario.id]}
            isLoading={isLoading}
            onStart={handleStart}
          />
        ))}

        {scenarios.length === 0 && !isLoading && (
          <Text color="fg.muted" fontSize="sm" textAlign="center">
            {t('noScenarios')}
          </Text>
        )}
      </VStack>
    </Box>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function ScenarioCard({
  scenario,
  bestScore,
  isLoading,
  onStart,
}: {
  scenario: TrainingScenarioSummary;
  bestScore?: number;
  isLoading: boolean;
  onStart: (id: string) => Promise<void>;
}): React.ReactElement {
  const t = useTranslations('chatbot.training');

  return (
    <Box border="1px solid" borderColor="border.default" borderRadius="lg" padding="3">
      <VStack gap="2" align="stretch">
        <HStack justify="space-between">
          <Text fontWeight="semibold" fontSize="sm">
            {scenario.title}
          </Text>
          <Badge size="sm" colorPalette={DIFFICULTY_COLORS[scenario.difficulty] ?? 'gray'}>
            {t(`difficulty.${scenario.difficulty}` as 'difficulty.beginner')}
          </Badge>
        </HStack>

        <HStack gap="2" fontSize="xs" color="fg.muted">
          <Badge size="sm" variant="outline">
            {CATEGORY_KEYS[scenario.category] ? t(CATEGORY_KEYS[scenario.category] as 'categories.dispatch') : scenario.category}
          </Badge>
          <HStack gap="1">
            <LuClock size={12} />
            <Text>{t('minLabel', { count: Math.floor(scenario.timeLimitSeconds / 60) })}</Text>
          </HStack>
          {bestScore !== undefined && (
            <HStack gap="1">
              <LuTrophy size={12} />
              <Text fontWeight="bold" color={bestScore >= 70 ? 'green.500' : 'orange.500'}>
                {bestScore}/100
              </Text>
            </HStack>
          )}
        </HStack>

        <Button
          size="xs"
          variant={bestScore ? 'outline' : 'primary'}
          onClick={() => onStart(scenario.id)}
          isLoading={isLoading}
        >
          <LuPlay size={12} />
          {bestScore ? t('retry') : t('start')}
        </Button>
      </VStack>
    </Box>
  );
}

function ScoreBar({ label, value }: { label: string; value: number }): React.ReactElement {
  const color = value >= 80 ? 'green' : value >= 50 ? 'orange' : 'red';

  return (
    <HStack gap="2" fontSize="sm">
      <Text flex="1" color="fg.muted">
        {label}
      </Text>
      <Box flex="2" bg="bg.subtle" borderRadius="full" height="2" overflow="hidden">
        <Box
          bg={`${color}.500`}
          height="full"
          borderRadius="full"
          width={`${value}%`}
          transition="width 0.5s"
        />
      </Box>
      <Text fontWeight="bold" width="10" textAlign="right">
        {value}
      </Text>
    </HStack>
  );
}
